/* RESPONSIBLE TEAM: team-self-serve */
import type AppService from 'embercom/services/app-service';
import Route from '@ember/routing/route';
import { inject as service } from '@ember/service';
import type Transition from '@ember/routing/transition';
import type RouterService from '@ember/routing/router-service';
import type GreatGuidanceService from 'embercom/services/great-guidance-service';
import type IntlService from 'embercom/services/intl';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';

export default class GettingStartedRoute extends Route {
  @service declare appService: AppService;
  @service declare greatGuidanceService: GreatGuidanceService;
  @service declare intl: IntlService;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare router: RouterService;

  private get isAiOnboardingEnabled(): boolean {
    // `buildRouteInfoMetadata()` is called by the router very early (for Sentry
    // team-tagging via responsibleTeamFromTransition) BEFORE beforeModel/model
    // run, when `appService.app` may still be undefined. Default to `false` in
    // that window — once the app loads, the actual route hooks evaluate against
    // the populated service. Mirrors inbox's `session.isWorkspaceLoaded` guard.
    return this.appService.app?.canUseFeature('team-self-serve-ai-onboarding') ?? false;
  }

  buildRouteInfoMetadata() {
    return {
      responsibleTeam: 'team-self-serve',
    };
  }

  async beforeModel() {
    if (this.isAiOnboardingEnabled) {
      this.router.replaceWith('apps.app.operator');
      return;
    }
    await this.finStandaloneService.initialize();
  }

  async model(_: $TSFixMe, transition: Transition) {
    let steps = await this.greatGuidanceService.fetchSteps(this.appService.app.id);
    if (transition.from?.name === 'apps_loading' && this.appService.app.isStandaloneApp) {
      let hasIncompleteSteps = steps.some((step) => !step.completed);

      if (!hasIncompleteSteps) {
        return this.transitionTo('apps.app.standalone.reports');
      }
    }
    return steps;
  }

  get titleToken() {
    return this.intl.t('standalone.navbar.getting-started');
  }
}
